/**
 * LARGEURS DE COLONNES — module PUR, mémorisé par utilisateur (Lot 4, 02/09/2026).
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ── Ce qu'il sert ──────────────────────────────────────────────────────────
 *
 * Le planning garde `table-layout: fixed` — indispensable aux performances sur
 * trente-deux lignes et plus, et c'est la contrainte posée par la spec. Les
 * largeurs sont donc pilotées par un état, jamais par le contenu.
 *
 * ⚠️ UNE LARGEUR MINIMALE PAR COLONNE, ET ELLE N'EST PAS DÉCORATIVE. Le Lot 1
 * a rendu les prénoms lisibles en récupérant de la largeur ; laisser resserrer
 * une colonne à trente pixels réintroduirait la troncature que ce travail vient
 * de fermer, et à la main cette fois. Le plancher est le prix d'un prénom.
 *
 * ⚠️ MÉMORISÉ PAR UTILISATEUR (`localStorage`), jamais par laboratoire : c'est
 * un confort de lecture qui dépend de l'écran de chacun. Le ranger au
 * paramétrage en ferait une décision de la RQ pour tout le monde.
 *
 * ⚠️ LE STOCKAGE PEUT ÉCHOUER en navigation privée. Il échoue alors EN SILENCE
 * et l'écran continue de fonctionner : une largeur non retenue est une gêne,
 * une exception non rattrapée est un écran blanc.
 */

/** En dessous, un prénom se tronque de nouveau — c'est le plancher du Lot 1. */
export const LARGEUR_MIN = 96;
/** Au-delà, une seule colonne pousse toutes les autres hors de l'écran. */
export const LARGEUR_MAX = 420;

export type Largeurs = Record<string, number>;

const CLE = 'bio24.planning.largeurs';

export const borner = (px: number) => Math.max(LARGEUR_MIN, Math.min(LARGEUR_MAX, Math.round(px)));

export function lireLargeurs(): Largeurs {
  try {
    const brut = localStorage.getItem(CLE);
    if (!brut) return {};
    const o = JSON.parse(brut) as unknown;
    if (!o || typeof o !== 'object') return {};
    const out: Largeurs = {};
    for (const [k, v] of Object.entries(o as Record<string, unknown>)) {
      /* On BORNE à la lecture aussi : une valeur écrite par une version
         antérieure, ou modifiée à la main dans le stockage, ne doit pas pouvoir
         rendre une colonne illisible. */
      if (typeof v === 'number' && Number.isFinite(v)) out[k] = borner(v);
    }
    return out;
  } catch { return {}; }
}

export function ecrireLargeurs(l: Largeurs): void {
  try { localStorage.setItem(CLE, JSON.stringify(l)); } catch { /* navigation privée */ }
}

export function oublierLargeurs(): void {
  try { localStorage.removeItem(CLE); } catch { /* navigation privée */ }
}
